import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  MessageSquare, 
  Send, 
  Settings, 
  Activity,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Phone,
  Edit,
  RefreshCw,
  TrendingUp,
  Calendar
} from 'lucide-react';
import { apiService } from '../../services/api';

export const WhatsAppControlePage = () => {
  const [statusApi, setStatusApi] = useState(null);
  const [estatisticas, setEstatisticas] = useState({});
  const [historico, setHistorico] = useState([]);
  const [mensagensPendentes, setMensagensPendentes] = useState([]);
  const [templateCobranca, setTemplateCobranca] = useState('');
  const [showEnvioManual, setShowEnvioManual] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Estados para envio manual
  const [numeroManual, setNumeroManual] = useState('');
  const [mensagemManual, setMensagemManual] = useState('');

  useEffect(() => {
    carregarDados();
    const interval = setInterval(carregarDados, 30000); // Atualizar a cada 30s
    return () => clearInterval(interval);
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [status, stats, hist, pendentes] = await Promise.all([
        apiService.getStatusWhatsAppApi(),
        apiService.getEstatisticasWhatsApp(),
        apiService.getHistoricoWhatsApp(),
        apiService.getMensagensPendentes()
      ]);
      
      setStatusApi(status);
      setEstatisticas(stats);
      setHistorico(hist);
      setMensagensPendentes(pendentes);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const enviarMensagemManual = async () => {
    if (!numeroManual || !mensagemManual) {
      alert('Preencha número e mensagem');
      return;
    }

    try {
      setLoading(true);
      await apiService.enviarMensagemManual({
        numero: numeroManual,
        mensagem: mensagemManual
      });
      
      alert('Mensagem enviada com sucesso!');
      setNumeroManual('');
      setMensagemManual('');
      setShowEnvioManual(false);
      carregarDados(); // Recarregar histórico
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      alert('Erro ao enviar mensagem');
    } finally {
      setLoading(false);
    }
  };

  const enviarCobrancaPendente = async (pendente) => {
    try {
      setLoading(true);
      await apiService.enviarCobrancaWhatsApp({
        numero: pendente.cliente_telefone,
        cliente_nome: pendente.cliente_nome,
        valor: pendente.valor,
        data_vencimento: pendente.data_vencimento,
        moto_placa: pendente.moto_placa,
        semana: 1
      });
      
      alert('Cobrança enviada!');
      carregarDados();
    } catch (error) {
      console.error('Erro ao enviar cobrança:', error);
      alert('Erro ao enviar cobrança');
    } finally {
      setLoading(false);
    }
  };

  const atualizarTemplate = async () => {
    try {
      await apiService.updateTemplateCobranca({
        template: templateCobranca,
        ativo: true
      });
      alert('Template atualizado!');
    } catch (error) {
      console.error('Erro ao atualizar template:', error);
      alert('Erro ao atualizar template');
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'enviado':
      case 'delivered_real':
        return <Badge className="bg-green-100 text-green-800">Enviado</Badge>;
      case 'falha':
        return <Badge className="bg-red-100 text-red-800">Falha</Badge>;
      case 'delivered_ready':
        return <Badge className="bg-blue-100 text-blue-800">Pronto Manual</Badge>;
      case 'delivered_simulated':
        return <Badge className="bg-yellow-100 text-yellow-800">Sistema Fallback</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">Processando</Badge>;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Controle WhatsApp Business</h1>
          <p className="text-gray-600">Central de gerenciamento completo de mensagens</p>
        </div>
        <div className="flex space-x-2">
          <Button onClick={carregarDados} disabled={loading} size="sm">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Dialog open={showEnvioManual} onOpenChange={setShowEnvioManual}>
            <DialogTrigger asChild>
              <Button>
                <Send className="h-4 w-4 mr-2" />
                Envio Manual
              </Button>
            </DialogTrigger>
          </Dialog>
        </div>
      </div>

      {/* Status da API */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            Status da API WhatsApp
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 border rounded-lg">
              <div className="text-2xl font-bold">
                {statusApi?.apis_configuradas?.callmebot ? 
                  <span className="text-green-600">✅ Ativo</span> : 
                  <span className="text-red-600">❌ Inativo</span>
                }
              </div>
              <div className="text-sm text-gray-600">CallMeBot API</div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{estatisticas.taxa_sucesso || 0}%</div>
              <div className="text-sm text-gray-600">Taxa de Sucesso</div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-2xl font-bold text-orange-600">{estatisticas.mensagens_hoje || 0}</div>
              <div className="text-sm text-gray-600">Mensagens Hoje</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="pendentes" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="pendentes">
            Pendentes ({mensagensPendentes.length})
          </TabsTrigger>
          <TabsTrigger value="historico">
            Histórico ({historico.length})
          </TabsTrigger>
          <TabsTrigger value="templates">
            Templates
          </TabsTrigger>
          <TabsTrigger value="estatisticas">
            Estatísticas
          </TabsTrigger>
        </TabsList>

        {/* Aba Pendentes */}
        <TabsContent value="pendentes">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="h-5 w-5 mr-2" />
                Cobranças Pendentes de Envio
              </CardTitle>
            </CardHeader>
            <CardContent>
              {mensagensPendentes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  Nenhuma cobrança pendente!
                </div>
              ) : (
                <div className="space-y-4">
                  {mensagensPendentes.map((pendente, index) => (
                    <div key={pendente.id || `${pendente.cliente_nome}-${index}`} className="border rounded-lg p-4 flex justify-between items-center">
                      <div className="flex-1">
                        <div className="font-medium">{pendente.cliente_nome}</div>
                        <div className="text-sm text-gray-600">
                          {pendente.cliente_telefone} • Moto: {pendente.moto_placa}
                        </div>
                        <div className="text-sm">
                          <span className="text-red-600 font-medium">R$ {pendente.valor?.toFixed(2)}</span>
                          {pendente.dias_atraso > 0 && (
                            <span className="ml-2 text-red-500">
                              • {pendente.dias_atraso} dias em atraso
                            </span>
                          )}
                        </div>
                      </div>
                      <Button 
                        onClick={() => enviarCobrancaPendente(pendente)}
                        disabled={loading}
                        size="sm"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Enviar Cobrança
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Histórico */}
        <TabsContent value="historico">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <MessageSquare className="h-5 w-5 mr-2" />
                Histórico de Mensagens
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {historico.map((msg, index) => (
                  <div key={msg.id || msg.timestamp || `hist-${index}`} className="border rounded-lg p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          {msg.cliente_nome || 'Mensagem Manual'}
                        </div>
                        <div className="text-xs text-gray-600">{msg.numero}</div>
                      </div>
                      {getStatusBadge(msg.status)}
                    </div>
                    <div className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                      {msg.mensagem?.substring(0, 100)}...
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {new Date(msg.data_envio).toLocaleString('pt-BR')}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Templates */}
        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Edit className="h-5 w-5 mr-2" />
                Template de Cobrança
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Mensagem de Cobrança</Label>
                <Textarea
                  value={templateCobranca}
                  onChange={(e) => setTemplateCobranca(e.target.value)}
                  placeholder="🏍️ *DESLOCA - Lembrete de Pagamento*&#10;&#10;Olá {cliente_nome}!&#10;&#10;💰 Seu pagamento vence em: *{data_vencimento}*&#10;📊 Valor: *R$ {valor}*&#10;🏍️ Moto: *{moto_placa}*"
                  rows={8}
                  className="font-mono text-sm"
                />
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-sm text-blue-800">
                  <strong>Variáveis disponíveis:</strong><br/>
                  {'{cliente_nome}, {valor}, {data_vencimento}, {moto_placa}, {semana}'}
                </div>
              </div>
              <Button onClick={atualizarTemplate}>
                Salvar Template
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Estatísticas */}
        <TabsContent value="estatisticas">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6 text-center">
                <TrendingUp className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                <div className="text-2xl font-bold">{estatisticas.total_mensagens || 0}</div>
                <div className="text-sm text-gray-600">Total de Mensagens</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                <div className="text-2xl font-bold">{estatisticas.mensagens_enviadas || 0}</div>
                <div className="text-sm text-gray-600">Enviadas com Sucesso</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <XCircle className="h-8 w-8 mx-auto mb-2 text-red-500" />
                <div className="text-2xl font-bold">{estatisticas.mensagens_falharam || 0}</div>
                <div className="text-sm text-gray-600">Falharam no Envio</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-orange-500" />
                <div className="text-2xl font-bold">{estatisticas.pendentes_cobranca || 0}</div>
                <div className="text-sm text-gray-600">Pendentes</div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal de Envio Manual */}
      <Dialog open={showEnvioManual} onOpenChange={setShowEnvioManual}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Envio Manual de Mensagem</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Número (com DDD)</Label>
              <Input
                value={numeroManual}
                onChange={(e) => setNumeroManual(e.target.value)}
                placeholder="11999999999"
              />
            </div>
            <div>
              <Label>Mensagem</Label>
              <Textarea
                value={mensagemManual}
                onChange={(e) => setMensagemManual(e.target.value)}
                placeholder="Digite sua mensagem personalizada..."
                rows={4}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={() => setShowEnvioManual(false)}
              >
                Cancelar
              </Button>
              <Button 
                onClick={enviarMensagemManual}
                disabled={loading}
              >
                {loading ? 'Enviando...' : 'Enviar Mensagem'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppControlePage;